<template>
  <div class="form-control w-full max-w-2xl mb-6">
    <!-- only show the label if it's not a checkbox -->
    <label v-if="showLabel" class="block mb-1 text-sm font-semibold text-base-content">
      {{ question.label }}
    </label>

    <component
      :is="componentType"
      :model-value="safeModelValue"
      @update:modelValue="value => emit('update:modelValue', value)"
      v-bind="question.props"
      class="transition focus:ring-2 focus:ring-primary focus:outline-none" />

    <p v-if="showHelp" class="text-xs italic text-base-content/60 border-l-2 border-primary/20 pl-3 mt-2">
      {{ question.help }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import TextInput from "@/components/forms/inputs/TextInput.vue";
import TextareaInput from "@/components/forms/inputs/TextareaInput.vue";
import SelectInput from "@/components/forms/inputs/SelectInput.vue";
import MultiSelect from "@/components/forms/inputs/MultiSelect.vue";
import CheckboxInput from "@/components/forms/inputs/CheckboxInput.vue";
import ColorPicker from "@/components/forms/inputs/ColorPicker.vue";
import MultiFileUpload from "@/components/forms/inputs/MultiFileUpload.vue";
import FileUpload from "@/components/forms/inputs/FileUpload.vue";
import Palette from "@/components/forms/inputs/Palette.vue";
import Font from "@/components/forms/inputs/FontPicker.vue";
import ListInput from "@/components/forms/inputs/ListInput.vue";
import { Question } from "../../schema/questions/Question";

const props = defineProps<{
  question: Question;
  modelValue: unknown;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: unknown): void;
}>();

const componentMap: Record<string, any> = {
  text: TextInput,
  textarea: TextareaInput,
  select: SelectInput,
  multiselect: MultiSelect,
  checkbox: CheckboxInput,
  colorpicker: ColorPicker,
  palette: Palette,
  file: FileUpload,
  multifile: MultiFileUpload,
  font: Font,
  listinput: ListInput,
};

const componentType = computed(() => componentMap[props.question.type] ?? TextInput);

const safeModelValue = computed(() => {
  const val = props.modelValue;
  switch (props.question.type) {
    case "text":
    case "textarea":
    case "select":
    case "colorpicker":
    case "font":
      return val ?? "";
    case "checkbox":
      return !!val;
    case "multiselect":
    case "palette":
      return val ?? "";
    case "listinput":
    case "multifile":
      return Array.isArray(val) ? val : [];
    case "file":
      return val ?? null;
    default:
      return val;
  }
});

const showLabel = computed(() => props.question.type !== "checkbox");
const showHelp = computed(() => !!props.question.help);
</script>
